import { type ReactNode } from "react";

import { useUIVersion } from "#hooks";
import { customTv } from "#utils";
import {
  ToggleButtonGroup as ReactAriaToggleButtonGroup,
  type ToggleButtonGroupProps as ReactAriaToggleButtonGroupProps,
  ToggleButton as ReactAriaToggleButton,
  type ToggleButtonProps as ReactAriaToggleButtonProps,
} from "react-aria-components";

export type { Key, Selection } from "react-aria-components";

export interface ToggleButtonGroupProps extends ReactAriaToggleButtonGroupProps {
  className?: string;
  children?: ReactNode;
}

const toggleButtonGroupStyles = customTv({
  base: "rounded-sm bg-background-secondary p-[.125rem] flex flex-row gap-x-xs w-fit",
  variants: {
    uiVersion: {
      v1: "",
      v2: "",
    },
  },
  compoundVariants: [
    {
      uiVersion: "v1",
      class: "rounded-xs border border-legacy-navy-L10 p-0 bg-background-primary gap-x-0",
    },
  ],
  defaultVariants: {
    uiVersion: "v2",
  },
});

export function ToggleButtonGroup({ className, ...props }: ToggleButtonGroupProps) {
  const uiVersion = useUIVersion();

  return (
    <ReactAriaToggleButtonGroup
      className={toggleButtonGroupStyles({ className, uiVersion })}
      {...props}
    >
      {props.children}
    </ReactAriaToggleButtonGroup>
  );
}

export interface ToggleButtonProps extends ReactAriaToggleButtonProps {
  className?: string;
}

const toggleButtonStyles = customTv({
  base: "rounded-sm px-md py-[.125rem] text-label-medium text-interactive-tertiary-button-enabled-foreground outline-none transition-all ease-in-out data-[selected]:bg-interactive-primary-button-enabled-background data-[selected]:text-interactive-primary-button-enabled-foreground hover:!bg-interactive-primary-button-hover-background hover:text-interactive-primary-button-enabled-foreground font-medium",
  variants: {
    uiVersion: {
      v1: "",
      v2: "",
    },
  },
  compoundVariants: [
    {
      uiVersion: "v1",
      class:
        "rounded-none text-label-medium-strong text-legacy-navy-L10 px-xs data-[selected]:bg-legacy-navy-L10 data-[selected]:text-neutral-white border-r border-r-legacy-navy-L10 last:border-r-0 hover:!bg-legacy-navy-L50",
    },
  ],
  defaultVariants: {
    uiVersion: "v2",
  },
});

export function ToggleButton({ children, className, ...props }: ToggleButtonProps) {
  const uiVersion = useUIVersion();

  return (
    <ReactAriaToggleButton className={toggleButtonStyles({ className, uiVersion })} {...props}>
      {children}
    </ReactAriaToggleButton>
  );
}
